import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import route from '@src/constants/route';
import { createProject } from '@src/apis/project';
import { useDispatch, useSelector } from 'react-redux';
import {
  PROJECT_OPTIONS_INPUT,
  PROJECT_OPTIONS_INPUT_NEW,
  TYPE_UPLOAD,
} from '@src/constants/upload';
import actions from '@src/redux/actions';
import { useTranslation } from 'react-i18next';
import { DEFAULT_BREAK_TIME, DEFAULT_SPEED } from '@src/constants/voice';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import apis from '@src/apis';
import { ContentState, EditorState } from 'draft-js';
import {
  useLocation,
  useHistory,
} from 'react-router-dom/cjs/react-router-dom.min';
import { BREAK_LINE_DELIMITER } from '@src/constants';
import { countTextLength, removeEmptyLineOfText } from '@src/services/tts';
import { INPUT_TTS_TYPE } from '@src/constants/tts';
import UploadFile from '@src/containers/TTS/UploadFile';
import ImportURL from '@src/containers/TTS/NewImportURL';
import {
  EXPIRED_PACKAGE_MAX_PROJECTS,
  PROJECT_PRODUCT,
} from '@src/constants/project';
import { trackProjectCreated } from '@src/services/moengage';
import { MOE_PROJECT_SOURCE_TYPE } from '@src/constants/moengage';
import ProjectInputItem from './ProjectInputItem';
import { StyledProjectInput } from './index.style';
import UploadFileBySrt from './UploadFileBySrt';
import UploadFileByPptx from './UploadFileByPptx';
import UploadFileByAudio from './UploadFileByAudio';
import UploadProjectFilePopup from './UploadProjectFilePopup';
import ImportProjectUrlPopup from './ImportProjectUrlPopup';

const ProjecInput = ({
  refetchProjects,
  handleOpenPopupProjectLimitReached,
  addProcessingProject,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { packageExpiryDate } = user;
  const maxProjects = user?.studioUsageOptions?.maxProjects;
  const history = useHistory();
  const [openUpload, setOpenUpload] = useState(false);
  const [openUploadBySrt, setOpenUploadBySrt] = useState(false);
  const [openUploadByPptx, setOpenUploadByPptx] = useState(false);
  const [openUploadByAudio, setOpenUploadByAudio] = useState(false);
  const [openImportUrl, setOpenImportUrl] = useState(false);
  const [openUrlPopup, setOpenUrlPopup] = useState(false);
  const [openFilePopup, setOpenFilePopup] = useState(false);
  const [filePopupVariant, setFilePopupVariant] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  const { getFeatureValue } = useFeatureFlags();

  const createSrtProjectEnabled = getFeatureValue(
    FEATURE_KEYS.CREATE_SRT_PROJECT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const newUiProjectsEnabled = getFeatureValue(FEATURE_KEYS.NEW_UI_PROJECTS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const uploadPresentationEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_PROJECTS,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const transcriptAudioEnabled = getFeatureValue(
    FEATURE_KEYS.TRANSCRIPT_AUDIO,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const videoDubbingProjectEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_PROJECT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const handleOpenUpload = () => setOpenUpload(true);
  const handleCloseUpload = () => setOpenUpload(false);

  const handleOpenImportUrl = () => setOpenImportUrl(true);
  const handleCloseImportUrl = () => setOpenImportUrl(false);

  const handleOpenUploadBySrt = () => setOpenUploadBySrt(true);
  const handleCloseUploadBySrt = () => setOpenUploadBySrt(false);

  const handleOpenUploadByPptx = () => setOpenUploadByPptx(true);
  const handleCloseUploadByPptx = () => setOpenUploadByPptx(false);

  const handleOpenUploadByAudio = () => setOpenUploadByAudio(true);
  const handleCloseUploadByAudio = () => setOpenUploadByAudio(false);

  const handleCreateProject = async (data) => {
    try {
      const res = await createProject(data);
      if (res.status) {
        // TODO: show toast
        refetchProjects(false);
      }
      return res;
    } finally {
      setIsCreating(false);
    }
  };

  // sourceType comes from the producing dialog (file_doc/file_pdf/file_txt/url)
  const handleUploadContent = async (text, sourceType) => {
    const handledText = removeEmptyLineOfText(text);

    const contentState = EditorState.createWithContent(
      ContentState.createFromText(handledText),
    ).getCurrentContent();
    const textContent = contentState.getPlainText('\n');
    const splitParagraphs = textContent.split(BREAK_LINE_DELIMITER);

    const paragraphs = splitParagraphs?.map((textParagraph) => ({
      id: uuidv4(),
      text: textParagraph || '',
      breakTime: DEFAULT_BREAK_TIME,
      speed: DEFAULT_SPEED,
      characters: countTextLength(textParagraph),
      elements: [
        {
          key: uuidv4(),
          text: textParagraph,
          startOffset: 0,
          endOffset: textParagraph.length,
          name: undefined,
          value: undefined,
        },
      ],
    }));

    const title = t('untitledTitleProject', {
      time: moment().format('YYYYMMDD-HH:mm'),
    });
    const data = {
      title,
      product: 'TTS',
      isDeleted: false,
      blocks: paragraphs,
    };
    const res = await handleCreateProject(data);
    if (res.status) {
      const idProject = res?.result?.project?.id;
      trackProjectCreated({
        product: data.product,
        sourceType,
      });
      history.push(`${route.PROJECTS}/${idProject}`);
      dispatch(actions.project.setProduct(PROJECT_PRODUCT.TTS));
    }
  };

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const handleCreateProjectBase = async () => {
    const data = {
      title: t('untitledTitleProject', {
        time: moment().format('YYYYMMDD-HH:mm'),
      }),
      product: 'TTS',
      isDeleted: false,
      blocks: [
        {
          id: uuidv4(),
          text: '',
          characters: 0,
          speed: 1,
          elements: [],
        },
      ],
    };
    const res = await handleCreateProject(data);
    if (res.status) {
      const idProject = res?.result?.project?.id;
      trackProjectCreated({
        product: data.product,
        sourceType: MOE_PROJECT_SOURCE_TYPE.EMPTY_TEXT,
      });
      history.push(`${route.PROJECTS}/${idProject}`);
      dispatch(actions.project.setProduct(PROJECT_PRODUCT.TTS));
    }
  };

  const getProjectsCount = async () => {
    const res = await apis.project.getProjectsCount();
    return res?.result?.count || 0;
  };

  const handleSelectOptionInput = async (_, item) => {
    setIsCreating(true);
    const isExpiredPackage = checkExpiredDate(packageExpiryDate);
    const effectiveLimit = isExpiredPackage
      ? EXPIRED_PACKAGE_MAX_PROJECTS
      : maxProjects;
    const hasProjectLimit = effectiveLimit > 0;
    const projectsCount = hasProjectLimit ? await getProjectsCount() : 0;
    const hasReachedProjectLimit =
      hasProjectLimit && projectsCount >= effectiveLimit;

    if (hasReachedProjectLimit) {
      handleOpenPopupProjectLimitReached(effectiveLimit);
      setIsCreating(false);
    } else {
      setIsCreating(false);
      switch (item.type) {
        case TYPE_UPLOAD.LINK:
          if (newUiProjectsEnabled) {
            setOpenUrlPopup(true);
          } else {
            handleOpenImportUrl();
          }
          break;
        case TYPE_UPLOAD.FILE:
          if (newUiProjectsEnabled) {
            setFilePopupVariant(TYPE_UPLOAD.FILE);
            setOpenFilePopup(true);
          } else {
            handleOpenUpload();
          }
          break;
        case 'TEXT':
          handleCreateProjectBase();
          break;
        case TYPE_UPLOAD.SRT:
          // SRT card no longer exists in PROJECT_OPTIONS_INPUT_NEW — but legacy
          // still fires this case when flag off. When flag on, .srt files are
          // accepted by the FILE variant (validate + processFileVariant branch
          // on extension), so just open the FILE popup.
          if (newUiProjectsEnabled) {
            setFilePopupVariant(TYPE_UPLOAD.FILE);
            setOpenFilePopup(true);
          } else {
            handleOpenUploadBySrt();
          }
          break;
        case TYPE_UPLOAD.PPTX:
          if (newUiProjectsEnabled) {
            setFilePopupVariant(TYPE_UPLOAD.PPTX);
            setOpenFilePopup(true);
          } else {
            handleOpenUploadByPptx();
          }
          break;
        case TYPE_UPLOAD.AUDIO:
          if (newUiProjectsEnabled) {
            setFilePopupVariant(TYPE_UPLOAD.AUDIO);
            setOpenFilePopup(true);
          } else {
            handleOpenUploadByAudio();
          }
          break;
        case TYPE_UPLOAD.VIDEO_DUBBING:
          if (newUiProjectsEnabled && videoDubbingProjectEnabled) {
            setFilePopupVariant(TYPE_UPLOAD.VIDEO_DUBBING);
            setOpenFilePopup(true);
          }
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const inputType = queryParams.get('inputType');
    switch (inputType) {
      case INPUT_TTS_TYPE.FILE:
        setOpenUpload(true);
        break;
      case INPUT_TTS_TYPE.LINK:
        setOpenImportUrl(true);
        break;
      default:
        break;
    }
  }, [location]);

  useEffect(
    () => () => {
      dispatch(actions.dubbingRequest.resetDubbingRequest());
    },
    [],
  );

  return (
    <StyledProjectInput newUiProjectsEnabled={newUiProjectsEnabled}>
      {(newUiProjectsEnabled
        ? PROJECT_OPTIONS_INPUT_NEW
        : PROJECT_OPTIONS_INPUT
      )
        ?.filter(
          (option) =>
            (option.type !== TYPE_UPLOAD.SRT || createSrtProjectEnabled) &&
            (option.type !== TYPE_UPLOAD.PPTX || uploadPresentationEnabled) &&
            (option.type !== TYPE_UPLOAD.AUDIO || transcriptAudioEnabled) &&
            (option.type !== TYPE_UPLOAD.VIDEO_DUBBING ||
              videoDubbingProjectEnabled),
        )
        .map((option) => (
          <ProjectInputItem
            key={option.title}
            logo={option.icon}
            title={t(option.label)}
            option={option}
            onSelect={handleSelectOptionInput}
            disabled={isCreating}
            isNewFeature={option?.isNewFeature}
            newUiProjectsEnabled={newUiProjectsEnabled}
            subText={t(option.subText)}
            subOptions={option.subOptions}
            type={option?.type}
          />
        ))}
      <UploadFile
        open={openUpload}
        onClose={handleCloseUpload}
        onHandleUploadContent={handleUploadContent}
      />
      <ImportURL
        open={openImportUrl}
        onClose={handleCloseImportUrl}
        onHandleUploadContent={handleUploadContent}
      />
      <UploadFileBySrt
        open={openUploadBySrt}
        onClose={handleCloseUploadBySrt}
        handleCreateProject={handleCreateProject}
      />
      {uploadPresentationEnabled && (
        <UploadFileByPptx
          open={openUploadByPptx}
          onClose={handleCloseUploadByPptx}
          handleCreateProject={handleCreateProject}
          addProcessingProject={addProcessingProject}
        />
      )}
      {transcriptAudioEnabled && (
        <UploadFileByAudio
          open={openUploadByAudio}
          onClose={handleCloseUploadByAudio}
          handleCreateProject={handleCreateProject}
        />
      )}
      {newUiProjectsEnabled && (
        <UploadProjectFilePopup
          open={openFilePopup}
          onClose={() => setOpenFilePopup(false)}
          variant={filePopupVariant}
        />
      )}
      {newUiProjectsEnabled && (
        <ImportProjectUrlPopup
          open={openUrlPopup}
          onClose={() => setOpenUrlPopup(false)}
        />
      )}
    </StyledProjectInput>
  );
};

export default ProjecInput;
